import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Tooltip,
  DatePicker,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";
import Utilities from "../../dependencies/custom/react-utilities";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, memo, useMemo, useRef } from "react";
import Settings from "../../dependencies/custom/settings";
import ApplicationProcessingPage from "../../components/comps/application_summary";
import useTable from "../../hooks/table";
import useDrawer from "../../hooks/drawer";
import CustomFunctions from "../../dependencies/custom/custom-functions";
import ValuesStore from "../../store/values-store";
import { useLocation } from "react-router-dom";
import isBetween from "dayjs/plugin/isBetween";
dayjs.extend(isBetween);

const { RangePicker } = DatePicker;
function Applicants({ status, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const drawer = useDrawer();
  const valuesStore = ValuesStore();
  const [filesFromDB, setFilesFromDB] = useState([]);
  const [formDBInfoGroups, setFormDBInfoGroups] = useState();

  const table = useTable(
    {
      pagination: {
        current: 1,
        pageSize: 20,
        position: ["bottomRight", "topRight"],
        hideOnSinglePage: true,
      },
    },
    undefined,
    undefined,
    undefined,
    undefined,
    "admissionID",
  );

  const columns = [
    {
      title: "Code",
      dataIndex: "application_code",
      ...table.getColumnSearchProps("application_code"),
    },
    {
      title: "FIRSTNAME",
      dataIndex: "firstName",
      ...table.getColumnSearchProps("firstName"),
    },
    {
      title: "SURNAME",
      dataIndex: "lastName",
      ...table.getColumnSearchProps("lastName"),
    },
    {
      title: "DOB",
      dataIndex: "dob",
      render: (v, record) => {
        return dayjs(v.dob).format("DD/MM/YYYY");
      },
      ...table.getColumnSearchProps("dob"),
    },
    {
      title: "GENDER",
      dataIndex: "gender",
    },
    {
      title: "CLASS",
      dataIndex: "className",
      ...table.getColumnSearchProps("className"),
    },
    {
      title: "D/B",
      dataIndex: "dayBoarding",
      ...table.getColumnSearchProps("dayBoarding"),
    },
    {
      title: "Application Date",
      dataIndex: "applicationDate",
      key: "applicationDate",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            value={selectedKeys[0]}
            onChange={(dates) => setSelectedKeys(dates ? [dates] : [])}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Filter
          </Button>
          <Button
            onClick={() => clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) => {
        if (!value || value.length !== 2) return true;
        const [start, end] = value;
        const recordDate = dayjs(record.applicationDate, "YYYY-MM-DD");
        return recordDate.isBetween(start, end, "day", "[]");
      },
      render: (v) => dayjs(v, "YYYY-MM-DD").format("DD/MM/YYYY"),
    },

    {
      title: "ACTIONS",
      key: "action",
      render: (v, record) => {
        return (
          <Space size="middle">
            <Tooltip title="Process Applicant">
              <Button
                className="border-0"
                type="primary"
                onClick={(e) => view(record)}
                icon={<i className="fas fa-cog" />}
              >
                {" "}
                Process
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    bootstrap();
    table.setColumns(columns);
    reset();
  }, [location.pathname /*table.tableParams*/]);

  function reset() {
    setFormDBInfoGroups({});
    setFilesFromDB([]);
  }

  async function bootstrap(options = {}) {
    if (options?.keepDrawerOpen !== true) drawer?.setOpen(false);
    table.setLoading(true);
    // const res = await Utilities.fetchWithAuth('GET', `${Settings.urls.enrollment.student}?status=${status}&has_paid=1`, {}, navigate);
    const res = await Utilities.fetchWithAuth(
      "GET",
      `${Settings.urls.enrollment.student}?status=${status}`,
      {},
      navigate,
    );

    if (res?.ok) {
      table.setData(res?.data);
      valuesStore.setValue("tableData", res?.data);
    }
    table.setAllowSelection(true);
    table.setLoading(false);
  }

  async function bulkCancel() {
    const ids = table.selectedRowKeys;
    if (!ids?.length) {
      Utilities.showNotification(undefined, "Select at least one application");
      return;
    }
    const res = await Utilities.fetchWithAuth(
      "PATCH",
      Settings.urls.enrollment.admissionBulkCancel,
      { admissionIDs: ids },
      navigate,
    );
    if (res?.ok) {
      Utilities.showNotification(undefined, res?.data?.message, "text-success");
      bootstrap();
    } else {
      Utilities.showNotification(undefined, res?.msg || "Operation failed");
    }
  }

  async function bulkDelete() {
    const ids = table.selectedRowKeys;
    if (!ids?.length) {
      Utilities.showNotification(undefined, "Select at least one application");
      return;
    }
    const res = await Utilities.fetchWithAuth(
      "DELETE",
      Settings.urls.enrollment.admissionBulkDelete,
      { admissionIDs: ids },
      navigate,
    );
    if (res?.ok) {
      Utilities.showNotification(undefined, res?.data?.message, "text-success");
      bootstrap();
    } else {
      Utilities.showNotification(undefined, res?.msg || "Operation failed");
    }
  }

  async function view(row) {
    table.setLoading(true);
    const admissionID = row?.admissionID;
    await loadAdmissionData(admissionID);
    table.setLoading(false);
    drawer.setTitle(`Processing ${row?.application_code} / ${admissionID}`);
    drawer.setOpen(true);
    drawer.setWidth(700);
    drawer.setPlacement("right");
  }

  async function loadAdmissionData(admissionID) {
    if (!admissionID) return;
    const result = await CustomFunctions.getDataFromDB(admissionID, navigate);
    const { dataInGroups = null, uploadedFiles = null } = result || {};
    const uf = CustomFunctions.setFilesObject(uploadedFiles);
    setFilesFromDB(uf);
    setFormDBInfoGroups(dataInGroups);
  }

  async function refreshCurrentApplication() {
    const admissionID = formDBInfoGroups?.student?.admissionID;
    await loadAdmissionData(admissionID);
  }

  return (
    <>
      <div className="">
        <Row gutter={[24, 0]}>
          <Col xs={24} xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace p-3"
              title={title}
            >
              <Space className="mb-3">
                <Popconfirm
                  title="Cancel selected applications?"
                  onConfirm={bulkCancel}
                >
                  <Button icon={<i className="fas fa-ban" />}>
                    Cancel Selected
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title="Permanently delete selected applications?"
                  onConfirm={bulkDelete}
                >
                  <Button danger icon={<i className="fas fa-trash" />}>
                    Delete Selected
                  </Button>
                </Popconfirm>
              </Space>
              <div className="table-responsive">
                {table.tableWithHeaderFooter(
                  CustomFunctions.tblCaption,
                  CustomFunctions.tblCaption,
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {drawer.drawerJSX(
        1500,
        <ApplicationProcessingPage
          applicant={formDBInfoGroups?.student}
          parent={formDBInfoGroups?.guardian}
          emergency={formDBInfoGroups?.emergency}
          interview={formDBInfoGroups?.interview}
          files={{
            filesFromDB: filesFromDB,
            categoryRules: CustomFunctions.categoryRules,
          }}
          currentStatus={status}
          bootstrap={bootstrap}
          onRefreshApplication={refreshCurrentApplication}
        />,
      )}
    </>
  );
}

export default Applicants;
